import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ButtonPrimary,
  ButtonTertiary,
  FormCheckbox,
  Text,
  cssColorValue,
  cssSpacing,
} from '@spotify-internal/encore-web';
import { FilterItem } from '@helpers/filterItem';
import { useFilterDropdownCopy } from '@hooks/useFilterDropdownCopy';

const Container = styled.div`
  background: ${cssColorValue('backgroundElevatedBase')};
  border-radius: 4px;
  box-shadow: 0 4px 16px 0 rgb(0 0 0 / 20%);
  width: calc(100% + 150px);
  padding: ${cssSpacing('base')} ${cssSpacing('tighter-2')} 0;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssSpacing('tighter-2')};
`;

const HelpText = styled(Text)`
  display: block;
  padding-top: ${cssSpacing('tighter')};
  padding-bottom: ${cssSpacing('tighter')};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  padding: ${cssSpacing('tighter-2')} ${cssSpacing('tighter-4')};
  justify-content: space-between;
  border-top: 1px solid ${cssColorValue('decorativeSubdued')};
`;

export interface FilterFlyoutProps {
  items: FilterItem[];
  initialSelection: string[];
  name: string;
  onApply: (selectedOptions: string[]) => void;
  helpText?: string;
  'aria-label'?: string;
}

export const FilterFlyout = ({
  items,
  initialSelection,
  name,
  onApply,
  helpText,
  'aria-label': ariaLabel,
}: FilterFlyoutProps) => {
  const copy = useFilterDropdownCopy();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState(() => new Set(initialSelection));

  useEffect(() => {
    const firstCheckbox = containerRef.current?.querySelector<HTMLInputElement>(
      'input[type="checkbox"]',
    );
    firstCheckbox?.focus();
  }, []);

  const toggleItem = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Container role="dialog" aria-label={ariaLabel} ref={containerRef}>
      <CheckboxList>
        {items.map(item => (
          <FormCheckbox
            key={item.id}
            id={`filter-${name}-${item.id}`}
            name={name}
            checked={selected.has(item.id)}
            onChange={() => toggleItem(item.id)}
          >
            {item.label}
          </FormCheckbox>
        ))}
      </CheckboxList>
      {helpText && (
        <HelpText variant="marginal" semanticColor="textSubdued">
          {helpText}
        </HelpText>
      )}
      <Footer>
        <ButtonTertiary
          size="small"
          onClick={() => setSelected(new Set())}
          condensed
        >
          {copy.clear}
        </ButtonTertiary>
        <ButtonPrimary
          size="small"
          onClick={() => onApply(Array.from(selected))}
        >
          {copy.apply}
        </ButtonPrimary>
      </Footer>
    </Container>
  );
};
